import { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { Vehicle } from '../../../data/vehicles';
import { upsertVehicles } from '../../../lib/vehicleService';

interface ImportExcelModalProps {
  onClose: () => void;
  onImport: () => void;
  existingCount: number;
}

interface ParsedRow {
  vin: string;
  name: string;
  year: number;
  price: number;
  condition: string;
  transmission: string;
  mileage: number;
  trim: string;
  fuel: string;
  color: string;
  images: string[];
  model?: string;
  facebookLink?: string;
  location?: string;
}

const MAX_IMAGES = 26;
const REQUIRED_COLS = ['vin', 'name', 'price', 'mileage', 'fuel', 'color', 'image1'];

function normalizeKey(key: string): string {
  return key.toLowerCase().replace(/[\s_\-\.]+/g, '');
}

function mapHeaders(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  const aliases: Record<string, string[]> = {
    vin: ['vin'],
    name: ['name', 'title', 'vehicle', 'vehiclename'],
    year: ['year', 'modelyear'],
    price: ['price', 'askingprice', 'saleprice', 'listprice'],
    condition: ['condition', 'status'],
    transmission: ['transmission', 'trans'],
    mileage: ['mileage', 'miles', 'odometer'],
    trim: ['trim', 'trimlevel', 'style'],
    fuel: ['fuel', 'fueltype'],
    color: ['color', 'colours', 'extcolor', 'exteriorcolor'],
    model: ['model', 'modelname', 'vehiclemodel'],
    facebookLink: ['facebooklink', 'facebook', 'fblink', 'fb'],
    location: ['location', 'lot', 'dealerlot'],
  };
  headers.forEach((h, idx) => {
    const norm = normalizeKey(h);
    for (const [field, aliasList] of Object.entries(aliases)) {
      if (aliasList.includes(norm) && !(field in map)) map[field] = idx;
    }
  });
  headers.forEach((h, idx) => {
    const norm = normalizeKey(h);
    for (let n = 1; n <= MAX_IMAGES; n++) {
      const key = `image${n}`;
      if ((norm === key || norm === `photo${n}` || norm === `img${n}`) && !(key in map)) map[key] = idx;
    }
  });
  return map;
}

function inferBrand(name: string): string {
  const brands = ['BMW','Chevrolet','Dodge','Ford','GMC','Honda','Hyundai','Infiniti','Jeep','Kia',
    'Land Rover','Mazda','Mercedes-Benz','Mitsubishi','Nissan','Ram','Tesla','Toyota','Audi','Buick',
    'Cadillac','Chrysler','Lincoln','Lexus','Acura','Subaru','Volkswagen','Volvo','Porsche'];
  const upper = name.toUpperCase();
  for (const b of brands) { if (upper.includes(b.toUpperCase())) return b; }
  return name.split(' ')[0] ?? 'Unknown';
}

function toCamelCase(str: string): string {
  if (!str) return str;
  return str
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function inferType(name: string, trim: string): string {
  const combined = `${name} ${trim}`.toUpperCase();
  if (/PICKUP|TRUCK|F-?150|F-?250|SILVERADO|SIERRA|RANGER|TACOMA|TUNDRA|RAM\s*\d/.test(combined)) return 'Truck';
  if (/VAN|PROMASTER|SPRINTER|TRANSIT|ODYSSEY|SIENNA|CARAVAN/.test(combined)) return 'Van';
  if (/CONVERTIBLE|CABRIOLET/.test(combined)) return 'Convertible';
  if (/COUPE|CAMARO|MUSTANG|CHALLENGER|CHARGER|CORVETTE/.test(combined)) return 'Coupe';
  if (/SUV|SPORT UTILITY|CX-|EXPLORER|EQUINOX|TERRAIN|TUCSON|SPORTAGE|KICKS|KONA|ESCAPE|PILOT|CR-V|RAV4|HIGHLANDER|TAHOE|SUBURBAN|EXPEDITION|NAVIGATOR|X5|X3|GLE|GLC|Q5|Q7/.test(combined)) return 'SUV';
  return 'Sedan';
}

function inferHorsepower(name: string, fuel: string): number {
  if (fuel === 'Electric') return 300;
  const upper = name.toUpperCase();
  if (/V8|HEMI/.test(upper)) return 370;
  if (/V6/.test(upper)) return 280;
  return 200;
}

function inferEngine(name: string, fuel: string): string {
  if (fuel === 'Electric') return 'Electric Motor';
  const upper = name.toUpperCase();
  if (/V8/.test(upper)) return '5.3L V8';
  if (/V6/.test(upper)) return '3.6L V6';
  return '2.0L I4';
}

function parseTextToRows(text: string): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return [];
  const firstLine = lines[0] ?? '';
  const tabCount = (firstLine.match(/\t/g) ?? []).length;
  const commaCount = (firstLine.match(/,/g) ?? []).length;
  const delimiter = tabCount >= commaCount ? '\t' : ',';
  return lines.map((line) => {
    if (delimiter === '\t') return line.split('\t').map((c) => c.trim());
    const result: string[] = [];
    let cur = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { inQuote = !inQuote; }
      else if (ch === ',' && !inQuote) { result.push(cur.trim()); cur = ''; }
      else { cur += ch; }
    }
    result.push(cur.trim());
    return result;
  });
}

function parseRowsToVehicles(rows: string[][]): { result: ParsedRow[]; errors: string[] } {
  if (rows.length < 2) return { result: [], errors: ['No data rows found.'] };
  const headers = rows[0].map(String);
  const colMap = mapHeaders(headers);
  const missing = REQUIRED_COLS.filter((c) => !(c in colMap));
  if (missing.length > 0) {
    return { result: [], errors: [`Missing required columns: ${missing.join(', ')}. Found: ${headers.slice(0, 15).join(', ')}`] };
  }
  const parseErrors: string[] = [];
  const result: ParsedRow[] = [];
  rows.slice(1).forEach((row, i) => {
    const rowNum = i + 2;
    const get = (field: string) => {
      if (!(field in colMap)) return '';
      return String(row[colMap[field]] ?? '').trim();
    };
    const vin = get('vin');
    const name = get('name');
    const priceRaw = get('price').replace(/[$,]/g, '');
    const mileageRaw = get('mileage').replace(/[,]/g, '');
    const yearRaw = get('year');
    const image1 = get('image1');
    if (!vin && !name) return;
    if (!vin) { parseErrors.push(`Row ${rowNum}: Missing VIN`); return; }
    if (!name) { parseErrors.push(`Row ${rowNum}: Missing vehicle name`); return; }
    if (!image1) { parseErrors.push(`Row ${rowNum}: Missing image URL (Image1)`); return; }
    const price = parseFloat(priceRaw);
    const mileage = parseInt(mileageRaw, 10);
    const year = parseInt(yearRaw, 10) || new Date().getFullYear();
    if (isNaN(price) || price <= 0) { parseErrors.push(`Row ${rowNum}: Invalid price "${get('price')}"`); return; }
    if (isNaN(mileage)) { parseErrors.push(`Row ${rowNum}: Invalid mileage "${get('mileage')}"`); return; }
    const images: string[] = [];
    for (let n = 1; n <= MAX_IMAGES; n++) {
      const key = `image${n}`;
      if (key in colMap) {
        const val = String(row[colMap[key]] ?? '').trim();
        if (val && val.startsWith('http')) images.push(val);
      }
    }
    if (images.length === 0) images.push(image1);
    result.push({
      vin, name, year, price,
      condition: get('condition') || 'Pre-Owned',
      transmission: get('transmission') || 'Automatic',
      mileage, trim: get('trim') || '',
      fuel: get('fuel') || 'Gasoline',
      color: get('color') || '—',
      images,
      model: 'model' in colMap ? toCamelCase(get('model')) || undefined : undefined,
      facebookLink: 'facebookLink' in colMap ? get('facebookLink') || undefined : undefined,
      location: 'location' in colMap ? get('location') || undefined : undefined,
    });
  });
  return { result, errors: parseErrors };
}

function buildVehicles(parsed: ParsedRow[]): Vehicle[] {
  return parsed.map((row, i) => ({
    id: i + 1,
    vin: row.vin,
    name: row.name,
    year: row.year,
    price: row.price,
    image: row.images[0],
    images: row.images,
    model: row.model ?? '',
    facebookLink: row.facebookLink,
    location: row.location,
    type: inferType(row.name, row.trim),
    mileage: row.mileage,
    transmission: row.transmission,
    fuel: row.fuel,
    brand: inferBrand(row.name),
    color: row.color,
    engine: inferEngine(row.name, row.fuel),
    horsepower: inferHorsepower(row.name, row.fuel),
    condition: row.condition,
    features: ['Rear Camera', 'Bluetooth', 'Cruise Control', 'Power Windows'],
    description: `${row.year} ${row.name.toUpperCase()}${row.trim ? ' ' + row.trim.toUpperCase() : ''}. Visit us for more details. Financing available!`,
    sold: false,
  }));
}

export default function ImportExcelModal({ onClose, onImport }: ImportExcelModalProps) {
  const [activeTab, setActiveTab] = useState<'file' | 'paste'>('file');
  const [dragOver, setDragOver] = useState(false);
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Paste / chunk state
  const [pasteText, setPasteText] = useState('');
  const [headerRow, setHeaderRow] = useState<string[] | null>(null);
  const [accumulatedDataRows, setAccumulatedDataRows] = useState<string[][]>([]);
  const [chunkCount, setChunkCount] = useState(0);
  const [pasteErrors, setPasteErrors] = useState<string[]>([]);
  const [pasteStatus, setPasteStatus] = useState('');

  const parseFile = useCallback((file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const isCsvOrTsv = /\.(csv|tsv|txt)$/i.test(file.name) || file.type === 'text/plain';
        let rows: string[][] = [];
        if (isCsvOrTsv) {
          const text = new TextDecoder('utf-8').decode(e.target?.result as ArrayBuffer);
          rows = parseTextToRows(text);
        } else {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as string[][];
        }
        if (rows.length < 2) { setErrors(['The file appears to be empty or has no data rows.']); return; }
        const { result, errors: errs } = parseRowsToVehicles(rows);
        if (errs.length > 0) { setErrors(errs); return; }
        if (result.length === 0) { setErrors(['No valid vehicle rows found in the file.']); return; }
        setErrors([]);
        setParsed(result);
        setStep('preview');
      } catch (err) {
        console.error('Parse error:', err);
        setErrors(['Failed to read the file. Please make sure it is a valid .xlsx, .xls, .csv, or .tsv file.']);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const handleAddChunk = () => {
    const text = pasteText.trim();
    if (!text) { setPasteErrors(['Please paste some data first.']); return; }
    setPasteErrors([]);
    const rows = parseTextToRows(text);
    if (rows.length === 0) { setPasteErrors(['No rows detected in the pasted text.']); return; }
    const firstRowNorm = rows[0].map(normalizeKey);
    const looksLikeHeader = firstRowNorm.includes('vin') || firstRowNorm.includes('name') || firstRowNorm.includes('price');
    if (looksLikeHeader) {
      setHeaderRow(rows[0]);
      const dataRows = rows.slice(1).filter((r) => r.some((c) => c.trim() !== ''));
      setAccumulatedDataRows((prev) => {
        const updated = [...prev, ...dataRows];
        setPasteStatus(`Header detected. Added ${dataRows.length} row(s). Total: ${updated.length}`);
        return updated;
      });
      setChunkCount((c) => c + 1);
    } else {
      if (!headerRow) { setPasteErrors(['Please paste the header row first (the row with VIN, Name, Price, etc.)']); return; }
      const dataRows = rows.filter((r) => r.some((c) => c.trim() !== ''));
      setAccumulatedDataRows((prev) => {
        const updated = [...prev, ...dataRows];
        setPasteStatus(`Added ${dataRows.length} row(s). Total: ${updated.length}`);
        return updated;
      });
      setChunkCount((c) => c + 1);
    }
    setPasteText('');
  };

  const handleFinalizePaste = () => {
    if (!headerRow) { setPasteErrors(['No header row found. Please paste the header row first.']); return; }
    if (accumulatedDataRows.length === 0) { setPasteErrors(['No vehicle data found. Please paste at least one data chunk.']); return; }
    const allRows = [headerRow, ...accumulatedDataRows];
    const { result, errors: errs } = parseRowsToVehicles(allRows);
    if (errs.length > 0) { setPasteErrors(errs); return; }
    if (result.length === 0) { setPasteErrors(['No valid vehicles found in the pasted data.']); return; }
    setPasteErrors([]);
    setParsed(result);
    setFileName(`Pasted data (${chunkCount} chunk${chunkCount !== 1 ? 's' : ''})`);
    setStep('preview');
  };

  const handleResetPaste = () => {
    setPasteText(''); setHeaderRow(null); setAccumulatedDataRows([]);
    setChunkCount(0); setPasteErrors([]); setPasteStatus('');
  };

  // ── Confirm import — saves directly to Supabase ──────────────────────────
  const handleConfirmImport = async () => {
    if (!parsed) return;
    setImporting(true);
    setImportError('');
    try {
      const vehicles = buildVehicles(parsed);
      await upsertVehicles(vehicles);
      window.dispatchEvent(new CustomEvent('pm_vehicles_updated'));
      setStep('done');
    } catch (err) {
      console.error('Import error:', err);
      setImportError('Failed to save to database. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = ['VIN','Name','Year','Price','Condition','Transmission','Mileage','Trim','Fuel','Color',
      ...Array.from({ length: MAX_IMAGES }, (_, i) => `Image${i + 1}`)];
    const sampleRow = ['1HGCM82633A123456','2020 Honda Accord','2020','22500','Pre-Owned','Automatic',
      '35000','EX-L Sedan 4D','Gasoline','SILVER / BLACK',
      'https://example.com/photo1.jpg','https://example.com/photo2.jpg',
      ...Array(MAX_IMAGES - 2).fill('')];
    const escape = (val: string) => `"${val.replace(/"/g, '""')}"`;
    const csvContent = [headers.map(escape).join(','), sampleRow.map(escape).join(',')].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = 'inventory_template.csv';
    document.body.appendChild(link); link.click();
    document.body.removeChild(link); URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-[#141414] border border-[#C9A84C]/30 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-[0_0_60px_rgba(201,168,76,0.12)] animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#C9A84C]/20 bg-[#C9A84C]/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C]/15 border border-[#C9A84C]/30 rounded-sm">
              <i className="ri-file-excel-2-line text-[#C9A84C] text-base"></i>
            </div>
            <div>
              <h2 className="text-sm font-bold text-white tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Import Inventory</h2>
              <p className="text-[10px] text-white/40 mt-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>Vehicles save directly to your database</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors cursor-pointer">
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        <div className="p-6">

          {/* ── STEP: Upload / Paste ── */}
          {step === 'upload' && (
            <>
              {/* Tabs */}
              <div className="flex gap-1 mb-5 p-1 bg-[#1a1a1a] border border-white/10 rounded-sm">
                {(['file', 'paste'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? 'bg-[#C9A84C] text-[#0d0d0d]' : 'text-white/50 hover:text-white'}`}
                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <i className={tab === 'file' ? 'ri-upload-2-line' : 'ri-clipboard-line'}></i>
                    {tab === 'file' ? 'Upload File' : 'Paste CSV'}
                  </button>
                ))}
              </div>

              {/* File Upload Tab */}
              {activeTab === 'file' && (
                <>
                  <div className="flex items-center justify-between mb-4 p-3 bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-sm">
                    <div className="flex items-center gap-2">
                      <i className="ri-download-2-line text-[#C9A84C] text-sm"></i>
                      <p className="text-xs text-white/70" style={{ fontFamily: 'Montserrat, sans-serif' }}>Not sure about the format? Download our template.</p>
                    </div>
                    <button
                      onClick={downloadTemplate}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-[10px] font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <i className="ri-file-excel-2-line text-xs"></i>Template
                    </button>
                  </div>

                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-sm p-10 text-center cursor-pointer transition-all ${dragOver ? 'border-[#C9A84C] bg-[#C9A84C]/10' : 'border-white/15 hover:border-[#C9A84C]/50 hover:bg-white/3'}`}
                  >
                    <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
                    <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 border border-white/10 rounded-sm bg-white/5">
                      <i className={`ri-upload-cloud-2-line text-3xl ${dragOver ? 'text-[#C9A84C]' : 'text-white/30'}`}></i>
                    </div>
                    <p className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {dragOver ? 'Drop your file here' : 'Click or drag & drop your file'}
                    </p>
                    <p className="text-xs text-white/40" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      Supports .xlsx, .xls, .csv · Up to 26 photos per vehicle
                    </p>
                  </div>

                  {errors.length > 0 && (
                    <div className="mt-4 p-4 bg-red-900/20 border border-red-700/40 rounded-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-error-warning-line text-red-400 text-sm"></i>
                        <p className="text-xs font-bold text-red-400 uppercase tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>Import Errors</p>
                      </div>
                      <ul className="space-y-1">
                        {errors.map((err, i) => <li key={i} className="text-xs text-red-300/80" style={{ fontFamily: 'Montserrat, sans-serif' }}>• {err}</li>)}
                      </ul>
                    </div>
                  )}

                  {/* Column reference */}
                  <div className="mt-5 p-4 bg-[#1a1a1a] border border-white/5 rounded-sm">
                    <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>Expected Column Headers</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                      {[
                        { col: 'VIN', req: true }, { col: 'Name', req: true }, { col: 'Year', req: false },
                        { col: 'Price', req: true }, { col: 'Condition', req: false }, { col: 'Transmission', req: false },
                        { col: 'Mileage', req: true }, { col: 'Trim', req: false }, { col: 'Fuel', req: true },
                        { col: 'Color', req: true }, { col: 'Image1', req: true }, { col: 'Image2 … Image26', req: false },
                        { col: 'Location', req: false }, { col: 'Facebook', req: false },
                      ].map(({ col, req }) => (
                        <div key={col} className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${req ? 'bg-[#C9A84C]' : 'bg-white/20'}`}></span>
                          <span className="text-[11px] text-white/60 font-mono">{col}</span>
                          {req && <span className="text-[9px] text-[#C9A84C]/60 uppercase tracking-wider" style={{ fontFamily: 'Montserrat, sans-serif' }}>required</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Paste CSV Tab */}
              {activeTab === 'paste' && (
                <>
                  <div className="mb-4 p-4 bg-[#C9A84C]/8 border border-[#C9A84C]/20 rounded-sm">
                    <p className="text-xs font-bold text-[#C9A84C] mb-2 tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>How to paste in chunks</p>
                    <ol className="space-y-1.5">
                      {[
                        'Paste the header row + first batch → click "Add Chunk"',
                        'Paste the next batch (no header needed) → click "Add Chunk"',
                        'Repeat until all vehicles are added',
                        'Click "Preview & Import" when done',
                      ].map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-white/60" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center bg-[#C9A84C]/20 text-[#C9A84C] rounded-full text-[10px] font-bold mt-0.5">{i + 1}</span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  </div>

                  {chunkCount > 0 && (
                    <div className="mb-3 flex items-center justify-between p-3 bg-[#1a1a1a] border border-[#C9A84C]/20 rounded-sm">
                      <div className="flex items-center gap-2">
                        <i className="ri-checkbox-circle-line text-[#C9A84C] text-sm"></i>
                        <span className="text-xs text-white/70" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          <span className="text-[#C9A84C] font-bold">{accumulatedDataRows.length}</span> vehicles across{' '}
                          <span className="text-[#C9A84C] font-bold">{chunkCount}</span> chunk{chunkCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <button onClick={handleResetPaste} className="text-[10px] text-white/30 hover:text-red-400 transition-colors cursor-pointer tracking-wider uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Reset</button>
                    </div>
                  )}

                  {pasteStatus && chunkCount > 0 && (
                    <div className="mb-3 px-3 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/20 rounded-sm">
                      <p className="text-[11px] text-[#C9A84C]" style={{ fontFamily: 'Montserrat, sans-serif' }}>{pasteStatus}</p>
                    </div>
                  )}

                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={chunkCount === 0
                      ? 'Paste your header row + first batch of vehicles here...'
                      : 'Paste the next batch of vehicles here (no header needed)...'}
                    className="w-full h-40 bg-[#1a1a1a] border border-white/10 focus:border-[#C9A84C] rounded-sm p-3 text-xs text-white/70 placeholder-white/20 focus:outline-none transition-all resize-none"
                    style={{ fontFamily: 'monospace' }}
                  />

                  {pasteErrors.length > 0 && (
                    <div className="mt-3 p-3 bg-red-900/20 border border-red-700/40 rounded-sm">
                      <ul className="space-y-1">
                        {pasteErrors.map((err, i) => <li key={i} className="text-xs text-red-300/80" style={{ fontFamily: 'Montserrat, sans-serif' }}>• {err}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleAddChunk}
                      disabled={!pasteText.trim()}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1a1a1a] border border-[#C9A84C]/40 hover:border-[#C9A84C] hover:bg-[#C9A84C]/10 text-[#C9A84C] rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <i className="ri-add-line text-sm"></i>Add Chunk
                    </button>
                    <button
                      onClick={handleFinalizePaste}
                      disabled={accumulatedDataRows.length === 0}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      <i className="ri-eye-line text-sm"></i>Preview &amp; Import
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ── STEP: Preview ── */}
          {step === 'preview' && parsed && (
            <>
              <div className="flex items-center gap-3 mb-4 p-3 bg-[#C9A84C]/10 border border-[#C9A84C]/25 rounded-sm">
                <i className="ri-checkbox-circle-line text-[#C9A84C] text-lg"></i>
                <div>
                  <p className="text-xs font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {parsed.length} vehicle{parsed.length !== 1 ? 's' : ''} ready to import
                  </p>
                  <p className="text-[10px] text-white/40 mt-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>From: {fileName}</p>
                </div>
              </div>

              <div className="space-y-2 max-h-72 overflow-y-auto sidebar-scroll pr-1">
                {parsed.map((row, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-white/5 rounded-sm hover:border-[#C9A84C]/20 transition-colors">
                    <div className="w-12 h-9 flex-shrink-0 rounded-sm overflow-hidden bg-[#0d0d0d]">
                      <img
                        src={`https://images.weserv.nl/?url=${encodeURIComponent(row.images[0])}&w=100&output=webp`}
                        alt={row.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-white truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>{row.year} {row.name}</p>
                      <p className="text-[10px] text-white/40 truncate" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {row.vin} · {row.mileage.toLocaleString()} mi · ${row.price.toLocaleString()}
                      </p>
                    </div>
                    {row.images.length > 1 && (
                      <span className="text-[9px] text-[#C9A84C]/80 bg-[#C9A84C]/10 border border-[#C9A84C]/20 px-1.5 py-0.5 rounded-sm whitespace-nowrap" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                        {row.images.length} photos
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {importError && (
                <div className="mt-4 p-3 bg-red-900/20 border border-red-700/40 rounded-sm">
                  <p className="text-xs text-red-300" style={{ fontFamily: 'Montserrat, sans-serif' }}>• {importError}</p>
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  onClick={() => { setParsed(null); setErrors([]); setImportError(''); setStep('upload'); handleResetPaste(); }}
                  disabled={importing}
                  className="flex-1 py-3 border border-white/15 hover:border-white/30 text-white/60 hover:text-white rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap disabled:opacity-40"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmImport}
                  disabled={importing}
                  className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-xs font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] transform disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {importing ? (
                    <><div className="w-4 h-4 border-2 border-[#0d0d0d]/30 border-t-[#0d0d0d] rounded-full animate-spin"></div>Saving...</>
                  ) : (
                    <>Import {parsed.length} Vehicle{parsed.length !== 1 ? 's' : ''}</>
                  )}
                </button>
              </div>
            </>
          )}

          {/* ── STEP: Done ── */}
          {step === 'done' && parsed && (
            <div className="py-6 text-center">
              <div className="w-20 h-20 flex items-center justify-center mx-auto mb-5 bg-[#C9A84C]/15 border-2 border-[#C9A84C]/40 rounded-full">
                <i className="ri-check-double-line text-4xl text-[#C9A84C]"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {parsed.length} Vehicle{parsed.length !== 1 ? 's' : ''} Saved!
              </h3>
              <p className="text-xs text-white/50 leading-relaxed mb-6 max-w-sm mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Your inventory has been saved to the database and is now live on your site — no publishing needed.
              </p>
              <div className="flex items-start gap-3 p-4 bg-[#C9A84C]/10 border border-[#C9A84C]/25 rounded-sm mb-6 text-left">
                <i className="ri-database-2-line text-[#C9A84C] text-lg mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-white/60 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  All <strong className="text-[#C9A84C]">{parsed.length} vehicles</strong> are permanently stored in your Supabase database. They will appear on your live site immediately and persist forever — even after republishing.
                </p>
              </div>
              <button
                onClick={() => { onImport(); }}
                className="w-full py-4 bg-[#C9A84C] hover:bg-[#e0bc5e] text-[#0d0d0d] rounded-sm text-sm font-bold tracking-widest uppercase transition-all cursor-pointer whitespace-nowrap hover:scale-[1.02] transform"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <i className="ri-car-line mr-2"></i>
                View My Inventory
              </button>
            </div>
          )}

        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.35s ease-out forwards; }
        .sidebar-scroll::-webkit-scrollbar { width: 3px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: #C9A84C30; border-radius: 2px; }
      `}</style>
    </div>
  );
}
